import { normalizeStage } from '../settings'
import type { Character, PlaygroundToolType, PrincipalCharacter } from '../types'

// ============================================================================
// CHARACTER REGISTRY - Single source of truth for all character data
// ============================================================================

export interface CharacterPromptExample {
  input: string
  output: string
}

export const BACKGROUND_SCALE_MODES = ['fixed-8-inch', 'scene-driven'] as const

export interface CharacterConfig {
  id: Character
  displayName: string
  fullName: string

  // Generation capabilities
  allowedTools: PlaygroundToolType[]
  supportsEC2: boolean
  hasScaleReference: boolean
  scaleReferenceKey?: string
  loraTrigger?: string
  /** WAN intro text. WAN is not Nano Banana and keeps the character's name; defaults to prompt.introDescription. */
  loraIntroDescription?: string

  // Prompt generation data (shared between WAN and NanoBanana templates)
  // Use {CHARACTER_NAME} placeholder in requiredFormat and example outputs —
  // WAN replaces with loraTrigger, NanoBanana replaces with characterIdentifier.
  // Fields that reach Nano Banana describe the character visually and never use its
  // name or brand: a named, well-known character pulls Gemini toward its own idea of
  // that character instead of the attached reference images.
  prompt: {
    characterIdentifier: string
    introDescription: string
    stylePrefix: string
    requiredFormat: string
    rules: string[]
    examples: CharacterPromptExample[]
  }

  // Background generation prompt metadata
  background: {
    characterName: string
    texture: string
    shape: string
    /** Omission preserves the legacy 8-inch, macro-perspective scene prompt. */
    scaleMode?: (typeof BACKGROUND_SCALE_MODES)[number]
  }

  // Frontend input placeholder for NanoBanana playground
  inputPlaceholder: string

  // Landing page card
  landing: {
    title: string
    description: string
    /** Hidden cards are omitted from the landing grid while the workflow remains available. */
    hidden?: boolean
  }

  // Stage visibility — if set, character only appears on these stages (undefined = all stages)
  stages?: string[]

  // Retired characters remain available to history, but disappear from active UI controls.
  retired?: boolean
}

// ============================================================================
// CHARACTER DEFINITIONS
// ============================================================================

const DOUGHBOY_CONFIG: CharacterConfig = {
  id: 'doughboy',
  displayName: 'Doughboy',
  fullName: 'the Pillsbury Doughboy',

  allowedTools: ['character-wan', 'character-nanobanana'],
  supportsEC2: true,
  hasScaleReference: true,
  scaleReferenceKey: 'scale-gemini/new_scale_ref.png',
  loraTrigger: 'd0ougb01blu3',
  loraIntroDescription: 'featuring the Pillsbury Doughboy character',

  prompt: {
    characterIdentifier: 'dough mascot character',
    introDescription: 'featuring the dough mascot character shown in the reference images',
    stylePrefix: 'Soft Focus 3D render, CGI',
    requiredFormat:
      '"Soft Focus 3D render, CGI, {CHARACTER_NAME}, [ACTION FROM USER INPUT], looking at the camera, creamy white skin tone with soft shading, minimal shadows, muted texture, centered in the frame, full shot, eyes and logo are the same blue shade #002e6d, [APPROPRIATE EXPRESSION], blue neck scarf tied at center, voluminous white chef\'s toque with blue logo, on a white background, advertising, soft diffused studio lighting."',
    rules: [
      "ALWAYS keep the character details (blue eyes #002e6d, blue scarf, white chef's toque)",
      'ALWAYS end with "on a white background, advertising, soft diffused studio lighting"',
      'Add an appropriate facial expression based on the action',
      'Return ONLY the prompt, no explanations'
    ],
    examples: [
      {
        input: 'shoveling snow',
        output:
          "Soft Focus 3D render, CGI, {CHARACTER_NAME}, shoveling snow with a snow shovel, looking at the camera, creamy white skin tone with soft shading, minimal shadows, muted texture, centered in the frame, full shot, eyes and logo are the same blue shade #002e6d, intense concentration with slightly furrowed brow, blue neck scarf tied at center, voluminous white chef's toque with blue logo, on a white background, advertising, soft diffused studio lighting."
      },
      {
        input: 'holding cookies',
        output:
          "Soft Focus 3D render, CGI, {CHARACTER_NAME}, Holding a tray of chocolate chip cookies, looking at the camera, creamy white skin tone with soft shading, minimal shadows, muted texture, centered in the frame, full shot, eyes and logo are the same blue shade #002e6d, look of pleasant surprise with eyes widened and mouth slightly open, blue neck scarf tied at center, voluminous white chef's toque with blue logo, on a white background, advertising, soft diffused studio lighting."
      },
      {
        input: 'painting',
        output:
          "Soft Focus 3D render, CGI, {CHARACTER_NAME}, painting on a canvas with a brush, looking at the camera, creamy white skin tone with soft shading, minimal shadows, muted texture, centered in the frame, full shot, eyes and logo are the same blue shade #002e6d, intense laughter, blue neck scarf tied at center, voluminous white chef's toque with blue logo, on a white background, advertising, soft diffused studio lighting."
      }
    ]
  },

  background: {
    characterName: 'the dough mascot character',
    texture: 'Smooth white dough (Standard).',
    shape:
      'Strictly observe Input 1. Use terms like "compact," "squat," "heavy bottomed," or "conical" to ensure the 8-inch proportion is maintained.'
  },

  inputPlaceholder: 'e.g., waving hello, doing a backflip, holding a birthday cake',

  landing: {
    title: 'Doughboy Workflow',
    description: 'Create a Doughboy image, place it in a scene, and make finishing edits—one step at a time.'
  },

  stages: ['local', 'dev', 'stg', 'prd', 'ale']
}

const CINNAMON_TOAST_CRUNCH_RULES: string[] = [
  'Preserve the golden-tan cereal surface, toasted cinnamon pattern, coarse sugar crystals, rounded square silhouette, and glossy brown eyes from the references.',
  'NEVER add arms, hands, legs, or feet, even when the user requests them. Express actions with the face, gaze, body orientation, or surrounding props without limbs.',
  'Keep the cereal body fairly rigid and square by default. Allow bending, stretching, squashing, or other body deformation only when explicitly requested.',
  'Allow varied facial expressions, eye directions, brows, and mouth shapes. A pink tongue may appear when the expression calls for it.',
  'Use a neutral gray studio backdrop, soft studio lighting, and a grounded shadow by default. An explicit user background or lighting request takes precedence.',
  'Honor explicit user size requests; never invent a measured height or force a macro lens.',
  'Return ONLY the finished prompt. Resolve all bracketed instructions; do not output placeholders.'
]

const CINNAMON_TOAST_CRUNCH_CONFIG: CharacterConfig = {
  id: 'cinnamon-toast-crunch',
  displayName: 'Cinnamon Toast Crunch',
  fullName: 'Cinnamon Toast Crunch cereal character',
  allowedTools: ['character-nanobanana'],
  supportsEC2: false,
  hasScaleReference: true,
  // Visual comparisons establish normal size; explicit user size requests take priority.
  scaleReferenceKey: 'character-scale-reference/cinnamon-toast-crunch/scale-v1.webp',

  prompt: {
    characterIdentifier: 'cinnamon cereal character',
    introDescription: 'featuring the cinnamon cereal character shown in the reference images',
    stylePrefix: 'Detailed 3D render, CGI',
    requiredFormat:
      '"Detailed 3D render, CGI, {CHARACTER_NAME}, [ACTION FROM USER INPUT], golden-tan cereal surface with toasted cinnamon marbling and coarse sparkling sugar crystals, thick rounded square silhouette, large glossy white eyes with brown irises, expressive cereal-material brow and mouth, [APPROPRIATE EXPRESSION], no arms, no hands, no legs, no feet, [ONE CHARACTER UNLESS A GROUP IS REQUESTED], [RIGID BODY UNLESS DEFORMATION IS REQUESTED], full character visible, [REQUESTED BACKGROUND OR NEUTRAL GRAY STUDIO BACKDROP], soft lighting and a grounded contact shadow."',
    rules: [
      ...CINNAMON_TOAST_CRUNCH_RULES,
      'Default to exactly one character. Include multiple characters only when the user explicitly requests a group or a specific count.'
    ],
    examples: [
      {
        input: 'smiling at the camera',
        output:
          'Detailed 3D render, CGI, {CHARACTER_NAME}, one fairly rigid thick rounded square cereal character smiling at the camera, golden-tan toasted surface with cinnamon marbling and coarse sparkling sugar crystals, large glossy white eyes with brown irises, cheerful cereal-material brow and mouth, no arms, no hands, no legs, no feet, full character visible on a neutral gray studio backdrop, soft studio lighting and a grounded contact shadow.'
      },
      {
        input: 'angry, looking to the right with its tongue out',
        output:
          'Detailed 3D render, CGI, {CHARACTER_NAME}, one fairly rigid thick rounded square cereal character looking right with an angry furrowed cereal brow and pink tongue sticking out, golden-tan toasted surface with cinnamon marbling and coarse sugar crystals, large glossy white eyes with brown irises, no arms, no hands, no legs, no feet, full character visible on a neutral gray studio backdrop, soft studio lighting and a grounded contact shadow.'
      },
      {
        input: 'three characters laughing on a blue kitchen counter',
        output:
          'Detailed 3D render, CGI, {CHARACTER_NAME}, three fairly rigid thick rounded square cereal characters laughing together on a blue kitchen counter, golden-tan toasted surfaces with cinnamon marbling and coarse sparkling sugar crystals, large glossy white eyes with brown irises and expressive cereal-material mouths, no arms, no hands, no legs, no feet on any character, all three fully visible, soft kitchen lighting and grounded contact shadows.'
      },
      {
        input: 'a giant character bending into an arch above a city street',
        output:
          'Detailed 3D render, CGI, {CHARACTER_NAME}, one giant cereal character bending its thick rounded square body into an arch above a city street as requested, golden-tan toasted surface with cinnamon marbling and coarse sugar crystals preserved across the bend, large glossy white eyes with brown irises and an excited cereal-material smile, no arms, no hands, no legs, no feet, full character visible, city buildings establish the requested giant scale, soft daylight and a grounded shadow.'
      }
    ]
  },

  background: {
    characterName: 'the cinnamon cereal character',
    texture:
      'Golden-tan toasted cereal with cinnamon marbling and coarse sparkling sugar crystals; glossy white eyes with brown irises and a cereal-material brow and mouth.',
    shape:
      'Use Input 1 for the thick rounded square silhouette, facial proportions, and normal cereal-piece physical size. The spoon, bowl, mug, and fingertip comparisons guide default size; explicit user size requests take priority. Default to one fairly rigid character with flexible expressions. Preserve explicitly requested groups and body deformation. NEVER add arms, hands, legs, or feet, even if requested.',
    scaleMode: 'scene-driven'
  },

  inputPlaceholder: 'e.g., smiling, looking surprised, three characters laughing, bending into an arch',
  landing: {
    title: 'Cinnamon Toast Crunch Workflow',
    description:
      'Create a Cinnamon Toast Crunch image, place it in a scene, and make finishing edits—one step at a time.'
  },
  stages: ['local', 'dev', 'ale']
}

const CHARACTER_REGISTRY: Readonly<Record<PrincipalCharacter, CharacterConfig>> = {
  doughboy: DOUGHBOY_CONFIG,

  'doughboy-2': {
    ...DOUGHBOY_CONFIG,
    id: 'doughboy-2',
    displayName: 'Doughboy 2',
    landing: {
      title: 'Doughboy 2 Workflow',
      description: 'Create a Doughboy image, place it in a scene, and make finishing edits—one step at a time.'
    },
    stages: ['ale']
  },

  'doughboy-3': {
    ...DOUGHBOY_CONFIG,
    id: 'doughboy-3',
    displayName: 'Doughboy 3',
    landing: {
      title: 'Doughboy 3 Workflow',
      description: 'Create a Doughboy image, place it in a scene, and make finishing edits—one step at a time.'
    },
    stages: ['ale']
  },

  buzz: {
    id: 'buzz',
    displayName: 'Buzz',
    fullName: 'Buzz the Honey Nut Cheerios bee',

    allowedTools: ['character-nanobanana'],
    supportsEC2: false,
    hasScaleReference: false,

    prompt: {
      characterIdentifier: 'cartoon honey bee mascot',
      introDescription: 'featuring the animated honey bee mascot shown in the reference images',
      stylePrefix: 'Soft Focus 3D render, CGI',
      requiredFormat:
        '"Soft Focus 3D render, CGI, {CHARACTER_NAME}, [ACTION FROM USER INPUT], looking at the camera, bright yellow and black striped body, translucent wings, large friendly eyes, cheerful antenna, centered in the frame, full shot, [APPROPRIATE EXPRESSION], on a white background, advertising, soft diffused studio lighting."',
      rules: [
        'ALWAYS keep the character details (yellow and black stripes, translucent wings, large friendly eyes, cheerful antenna)',
        'ALWAYS end with "on a white background, advertising, soft diffused studio lighting"',
        'Add an appropriate facial expression based on the action',
        'Return ONLY the prompt, no explanations'
      ],
      examples: [
        {
          input: 'waving hello',
          output:
            'Soft Focus 3D render, CGI, {CHARACTER_NAME}, waving hello with one wing raised, looking at the camera, bright yellow and black striped body, translucent wings, large friendly eyes, cheerful antenna, centered in the frame, full shot, wide cheerful smile with eyes slightly squinted in happiness, on a white background, advertising, soft diffused studio lighting.'
        },
        {
          input: 'holding a bowl of cereal',
          output:
            'Soft Focus 3D render, CGI, {CHARACTER_NAME}, holding a bowl of honey-glazed oat cereal rings with both hands, looking at the camera, bright yellow and black striped body, translucent wings, large friendly eyes, cheerful antenna, centered in the frame, full shot, enthusiastic expression with raised eyebrows and big smile, on a white background, advertising, soft diffused studio lighting.'
        },
        {
          input: 'doing a backflip',
          output:
            'Soft Focus 3D render, CGI, {CHARACTER_NAME}, doing a backflip mid-air with wings spread, looking at the camera, bright yellow and black striped body, translucent wings, large friendly eyes, cheerful antenna, centered in the frame, full shot, exhilarated expression with a wide open-mouth grin, on a white background, advertising, soft diffused studio lighting.'
        }
      ]
    },

    background: {
      characterName: 'the cartoon honey bee mascot',
      texture: 'Bright yellow and black striped body, translucent wings, large friendly eyes, cheerful antenna.',
      shape:
        "Observe Input 1 for wing proportions, body segmentation, and overall bee anatomy. Maintain the character's insect-like proportions."
    },

    inputPlaceholder: 'e.g., flying through a field of flowers, holding a honey jar, waving hello',

    landing: {
      title: 'Buzz Workflow',
      description: 'Create a Buzz image, place it in a scene, and make finishing edits—one step at a time.'
    },

    stages: ['local', 'dev', 'stg', 'prd', 'ale']
  },

  bowie: {
    id: 'bowie',
    displayName: 'Bowie',
    fullName: 'Bowie the silver tabby cat',

    allowedTools: ['character-nanobanana'],
    supportsEC2: false,
    hasScaleReference: false,

    prompt: {
      characterIdentifier: 'silver tabby cat',
      introDescription: 'featuring a real silver tabby cat with striking sapphire-blue eyes',
      stylePrefix: 'Professional studio photograph',
      requiredFormat:
        '"Professional studio photograph, {CHARACTER_NAME}, [ACTION FROM USER INPUT], looking at the camera, dense silver-grey fur with charcoal mackerel tabby stripes, deep sapphire-blue eyes, lighter off-white chest and underbelly, sturdy healthy build, centered in the frame, full shot, [APPROPRIATE EXPRESSION], on a white background, professional studio lighting."',
      rules: [
        'ALWAYS keep the character details (silver-grey fur with charcoal tabby stripes, sapphire-blue eyes, off-white chest/underbelly)',
        'ALWAYS end with "on a white background, professional studio lighting"',
        'This is a REAL cat, NOT CGI or 3D render — the output must look like a professional photograph',
        'Add an appropriate facial expression or body language based on the action',
        'Return ONLY the prompt, no explanations'
      ],
      examples: [
        {
          input: 'sitting on a windowsill',
          output:
            'Professional studio photograph, {CHARACTER_NAME}, sitting gracefully on a windowsill with front paws neatly tucked, looking at the camera, dense silver-grey fur with charcoal mackerel tabby stripes, deep sapphire-blue eyes, lighter off-white chest and underbelly, sturdy healthy build, centered in the frame, full shot, serene and attentive expression with ears perked forward, on a white background, professional studio lighting.'
        },
        {
          input: 'playing with yarn',
          output:
            'Professional studio photograph, {CHARACTER_NAME}, playfully batting at a ball of yarn with one paw raised, looking at the camera, dense silver-grey fur with charcoal mackerel tabby stripes, deep sapphire-blue eyes, lighter off-white chest and underbelly, sturdy healthy build, centered in the frame, full shot, wide-eyed curious expression with dilated pupils, on a white background, professional studio lighting.'
        },
        {
          input: 'wearing a tiny hat',
          output:
            'Professional studio photograph, {CHARACTER_NAME}, wearing a tiny top hat perched between the ears, looking at the camera, dense silver-grey fur with charcoal mackerel tabby stripes, deep sapphire-blue eyes, lighter off-white chest and underbelly, sturdy healthy build, centered in the frame, full shot, dignified expression with a slight head tilt, on a white background, professional studio lighting.'
        }
      ]
    },

    background: {
      characterName: 'the silver tabby cat',
      texture:
        'Dense silver-grey fur with charcoal mackerel tabby stripes, deep sapphire-blue eyes, lighter off-white chest and underbelly.',
      shape:
        "Observe Input 1 for body proportions, tail length, and overall feline anatomy. Maintain the character's natural cat proportions and sturdy build."
    },

    inputPlaceholder: 'e.g., sitting on a windowsill, chasing a laser pointer, wearing a bow tie',

    landing: {
      title: 'Bowie Workflow',
      description: 'Create a Bowie image, place it in a scene, and make finishing edits—one step at a time.'
    },

    stages: ['local', 'ale']
  },

  'cinnamon-toast-crunch': CINNAMON_TOAST_CRUNCH_CONFIG,

  'cinnamon-toast-crunch-group': {
    ...CINNAMON_TOAST_CRUNCH_CONFIG,
    id: 'cinnamon-toast-crunch-group',
    displayName: 'Cinnamon Toast Crunch Group',
    fullName: 'Cinnamon Toast Crunch cereal characters',
    scaleReferenceKey: 'character-scale-reference/cinnamon-toast-crunch-group/scale-v1.webp',
    prompt: {
      ...CINNAMON_TOAST_CRUNCH_CONFIG.prompt,
      characterIdentifier: 'cinnamon cereal characters',
      introDescription: 'featuring a group of the cinnamon cereal characters shown in the reference images',
      requiredFormat:
        '"Detailed 3D render, CGI, {CHARACTER_NAME}, [MULTIPLE CHARACTERS BY DEFAULT; HONOR AN EXPLICIT COUNT INCLUDING ONE], [ACTION FROM USER INPUT], individual fairly rigid thick rounded square cereal bodies, golden-tan toasted surfaces with cinnamon marbling and coarse sparkling sugar crystals, large glossy white eyes with brown irises and varied expressive cereal-material brows and mouths, no arms, no hands, no legs, no feet on any character, distinct readable faces and separate bodies, all requested characters visible together in one frame, [REQUESTED BACKGROUND OR NEUTRAL GRAY STUDIO BACKDROP], soft lighting and grounded contact shadows."',
      rules: [
        ...CINNAMON_TOAST_CRUNCH_RULES,
        'Default to multiple distinct characters together in one frame. When no count is specified, choose a group count that fits the action and composition; do not force a fixed count.',
        'An explicit user count takes priority, including exactly one character when requested. Reference image counts are examples, not required output counts.',
        'Keep every character individually readable with a separate cereal body and one pair of eyes and one mouth. Vary expressions, gaze, and orientation naturally without fusing bodies, duplicating faces on a body, or adding limbs.',
        'Generate a single coherent scene, never a reference grid, collage, or multiple panels. Fit the requested group in the frame without unintentionally cropping members.'
      ],
      examples: [
        {
          input: 'laughing together',
          output:
            'Detailed 3D render, CGI, {CHARACTER_NAME}, a group of distinct fairly rigid thick rounded square cereal characters laughing together, golden-tan toasted surfaces with cinnamon marbling and coarse sparkling sugar crystals, large glossy white eyes with brown irises and individually varied happy cereal-material mouths, no arms, no hands, no legs, no feet on any character, separate readable bodies and faces, the whole group visible in one frame on a neutral gray studio backdrop, soft lighting and grounded contact shadows.'
        },
        {
          input: 'five characters looking surprised beside a coffee mug',
          output:
            'Detailed 3D render, CGI, {CHARACTER_NAME}, exactly five distinct small cereal characters gathered beside a normal full-sized coffee mug, each a fairly rigid thick rounded square with golden-tan cinnamon marbling and coarse sugar crystals, glossy white eyes with brown irises and individually surprised cereal-material brows and mouths, no arms, hands, legs, or feet, all five bodies and faces readable together in one frame, the mug much larger than each cereal piece, soft daylight and grounded contact shadows.'
        },
        {
          input: 'exactly one giant character smiling above a city street',
          output:
            'Detailed 3D render, CGI, {CHARACTER_NAME}, exactly one giant fairly rigid thick rounded square cereal character smiling above a city street as explicitly requested, golden-tan toasted surface with cinnamon marbling and coarse sparkling sugar crystals, glossy white eyes with brown irises and a cheerful cereal-material smile, no arms, no hands, no legs, no feet, full character visible, buildings establish the requested giant scale, soft daylight and a grounded shadow.'
        }
      ]
    },
    background: {
      ...CINNAMON_TOAST_CRUNCH_CONFIG.background,
      characterName: 'the group of cinnamon cereal characters',
      shape:
        'Use Input 1 for each thick rounded square cereal body, facial proportions, and normal cereal-piece physical size. Spoon, bowl, mug, and fingertip comparisons guide default size; explicit user size requests take priority. Scale-reference panel counts and arrangements do not set scene count or composition. Preserve the source group count, individual identities, poses, gaze, and arrangement unless explicitly changed. When no source or count is supplied, use multiple distinct characters without a fixed count. Honor any explicit count, including one. Keep bodies separate and faces readable. Keep bodies fairly rigid unless deformation is requested. NEVER add arms, hands, legs, or feet, even if requested.'
    },
    inputPlaceholder:
      'e.g., laughing together, five looking surprised, a crowd beside a bowl, exactly one giant character',
    landing: {
      title: 'Cinnamon Toast Crunch Group Workflow',
      description:
        'Create a group of Cinnamon Toast Crunch characters, place them in a scene, and make finishing edits.'
    }
  },

  buzzhands: {
    id: 'buzzhands',
    displayName: 'Buzz Hands',
    fullName: 'Buzz the Bee Hands (Honey Nut Cheerios)',

    allowedTools: ['character-nanobanana'],
    supportsEC2: false,
    hasScaleReference: false,

    prompt: {
      characterIdentifier: "cartoon honey bee mascot's hands",
      introDescription: 'featuring the hands and arms of the animated honey bee mascot shown in the reference images',
      stylePrefix: 'Soft Focus 3D render, CGI',
      requiredFormat:
        '"Soft Focus 3D render, CGI, a close-up of {CHARACTER_NAME}, [HAND POSE OR OBJECT INTERACTION FROM USER INPUT], smooth matte yellow skin texture, four-fingered hand, slender orange-yellow arm, red ribbed cuff at the wrist, include extreme detail on the held object when one is present, on a white background, advertising, soft diffused studio lighting."',
      rules: [
        'ALWAYS specify the four-fingered hand structure and smooth matte yellow texture',
        'ALWAYS include the "red ribbed cuff" at the base of the wrist/arm',
        'ALWAYS focus on the finger pose and hand action, including interaction with the object when one is present',
        'ALWAYS end with "on a white background, advertising, soft diffused studio lighting"',
        'Return ONLY the prompt, no explanations'
      ],
      examples: [
        {
          input: 'holding a single cereal ring',
          output:
            'Soft Focus 3D render, CGI, a close-up of {CHARACTER_NAME}, delicately holding a single honey-glazed oat cereal ring between thumb and forefinger, slender yellow arm, four-fingered hand with rounded fingertips, red ribbed fabric cuff at the wrist, matte skin texture, centered in the frame, extreme detail on the cereal texture, on a white background, advertising, soft diffused studio lighting.'
        },
        {
          input: 'giving a thumbs up',
          output:
            'Soft Focus 3D render, CGI, a close-up of {CHARACTER_NAME}, giving a confident thumbs up gesture, slender yellow arm, four-fingered hand with rounded fingertips, red ribbed fabric cuff at the wrist, matte skin texture, centered in the frame, on a white background, advertising, soft diffused studio lighting.'
        },
        {
          input: 'pointing at the viewer',
          output:
            'Soft Focus 3D render, CGI, a close-up of {CHARACTER_NAME}, pointing directly at the camera with the index finger, slender yellow arm, four-fingered hand with rounded fingertips, red ribbed fabric cuff at the wrist, matte skin texture, centered in the frame, foreshortened perspective, on a white background, advertising, soft diffused studio lighting.'
        }
      ]
    },

    background: {
      characterName: "the cartoon honey bee mascot's hands",
      texture:
        'Smooth, matte orange-yellow skin, non-glossy, with a distinct red fabric-textured ribbed cuff at the wrist.',
      shape:
        'Observe reference for slender limb thickness and the specific four-finger (three fingers and one thumb) hand morphology. The fingers are rounded and bulbous at the tips.'
    },

    inputPlaceholder: 'e.g., holding a spoon, pointing at a cereal box, catching a cheerio',

    landing: {
      title: 'Buzz Hands Workflow',
      description: 'Create a Buzz pose, place it in a scene, and make finishing edits—one step at a time.'
    },

    stages: ['local', 'dev', 'ale']
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const DEFAULT_CHARACTER: PrincipalCharacter = 'buzz'

export const PRINCIPAL_CHARACTER_IDS = Object.keys(CHARACTER_REGISTRY) as [PrincipalCharacter, ...PrincipalCharacter[]]

export function isPrincipalCharacter(character: string): character is PrincipalCharacter {
  return Object.hasOwn(CHARACTER_REGISTRY, character)
}

export interface CharacterConfigQueryOptions {
  includeRetired?: boolean
}

export function getCharacterConfig(character: PrincipalCharacter): CharacterConfig {
  return CHARACTER_REGISTRY[character]
}

/** Registry lookup for any ID; undefined for dynamic or unknown characters. */
export function getPrincipalCharacterConfig(character: string): CharacterConfig | undefined {
  return isPrincipalCharacter(character) ? CHARACTER_REGISTRY[character] : undefined
}

export function getAllCharacterConfigs(): CharacterConfig[] {
  return Object.values(CHARACTER_REGISTRY)
}

function getCharacterLabel(config: CharacterConfig): string {
  return config.retired ? `${config.displayName} (retired)` : config.displayName
}

export function getCharacterLabels(): Record<string, string> {
  const labels: Record<string, string> = {}
  for (const config of Object.values(CHARACTER_REGISTRY)) {
    labels[config.id] = getCharacterLabel(config)
  }
  return labels
}

export function getCharacterSelectOptions({ includeRetired = false }: CharacterConfigQueryOptions = {}): Array<{
  value: Character
  label: string
}> {
  return Object.values(CHARACTER_REGISTRY)
    .filter((config) => includeRetired || !config.retired)
    .map((config) => ({ value: config.id, label: getCharacterLabel(config) }))
}

export function getCharacterAllowedTools(character: PrincipalCharacter): PlaygroundToolType[] {
  return CHARACTER_REGISTRY[character].allowedTools
}

export function getCharacterConfigsForStage(
  stage: string,
  { includeRetired = false }: CharacterConfigQueryOptions = {}
): CharacterConfig[] {
  const normalized = normalizeStage(stage)
  const visibleStages = new Set([stage, normalized])
  return Object.values(CHARACTER_REGISTRY).filter(
    (config) =>
      (includeRetired || !config.retired) && (!config.stages || config.stages.some((entry) => visibleStages.has(entry)))
  )
}

export function getCharacterSelectOptionsForStage(
  stage: string,
  options?: CharacterConfigQueryOptions
): Array<{ value: Character; label: string }> {
  return getCharacterConfigsForStage(stage, options).map((config) => ({
    value: config.id,
    label: getCharacterLabel(config)
  }))
}
